import { useEffect, useState } from "react";import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Globe2, Plus, Trash2, Loader2, ChevronLeft, X, Handshake, TrendingUp,
} from "lucide-react";

const WINDOW_LABELS = {
  theatrical:     "Theatrical",
  home_video:     "Home Video",
  satellite_ott:  "Satellite · OTT",
  overseas:       "Overseas",
  music:          "Music",
  non_theatrical: "Non-Theatrical",
};
const STATUS_TONE = {
  negotiating: "bg-amber-500/20 text-amber-200",
  signed:      "bg-sky-500/20 text-sky-200",
  delivered:   "bg-emerald-500/20 text-emerald-200",
  recouped:    "bg-emerald-500/25 text-emerald-100",
  cancelled:   "bg-red-500/20 text-red-200",
};
const ACCENT = "#DB6D19";

/**
 * L13 · Sales Agent Deals — full ledger page.
 * Lists every rights sale the agent has closed. Each row shows the
 * film, buyer, territory, MG, commission split and status. Totals
 * roll up per currency at the top.
 */
export default function SalesAgentDeals() {
  const [org, setOrg] = useState(null);
  const [deals, setDeals] = useState([]);
  const [totals, setTotals] = useState({});
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [meta, setMeta] = useState({ buyer_types: [], currencies: [], statuses: [], window_kinds: [] });

  useEffect(() => {
    api.get("/onboard/me/organization").then(({ data }) => setOrg(data.organization)).catch(() => setOrg(null));
    api.get("/sales-agent/meta").then(({ data }) => setMeta(data)).catch(() => {});
    refresh();
  }, []);

  const refresh = () => {
    setLoading(true);
    api.get("/sales-agent/deals/mine")
      .then(({ data }) => { setDeals(data.deals || []); setTotals(data.totals || {}); })
      .catch(() => { setDeals([]); setTotals({}); })
      .finally(() => setLoading(false));
  };

  return (
    <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-10"
         data-testid="sales-agent-deals-page">
      <Link to="/dashboard" className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-[0.22em] text-white/55 hover:text-white transition"
            data-testid="sales-agent-deals-back">
        <ChevronLeft size={13}/> Back to Dashboard
      </Link>

      <div className="mt-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] tracking-[0.3em] uppercase text-white/55">L13 · Sales Agent</div>
          <h1 className="font-display text-4xl sm:text-5xl uppercase tracking-tight mt-2"
              style={{ color: ACCENT }}
              data-testid="sales-agent-deals-heading">
            Deals Ledger
          </h1>
          <p className="mt-3 text-sm text-white/60 max-w-xl leading-relaxed">
            Every rights sale you close, with MG, expenses, commission
            split and status. Totals roll up per currency below.
          </p>
        </div>
        <Button className="h-10 rounded-full text-[11px] uppercase tracking-[0.22em] bg-orange-600 text-white hover:bg-orange-500"
                onClick={() => setShowAdd(true)}
                data-testid="sales-agent-deals-add-btn">
          <Plus size={13} className="mr-1.5"/> Log Deal
        </Button>
      </div>

      {/* Totals per currency */}
      {Object.keys(totals).length > 0 && (
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-4"
             data-testid="sales-agent-deals-totals">
          {Object.entries(totals).map(([cur, t]) => (
            <div key={cur} className="cs-card rounded-xl p-5"
                 data-testid={`sales-agent-deals-totals-${cur}`}>
              <div className="flex items-center justify-between">
                <div className="text-[10px] tracking-[0.28em] uppercase text-white/45">{cur} · {t.count} deal{t.count === 1 ? "" : "s"}</div>
                <TrendingUp size={14} className="text-orange-300"/>
              </div>
              <div className="mt-3 font-display text-2xl uppercase tracking-tight" style={{ color: ACCENT }}>
                {fmtMoney(t.mg, cur)}
              </div>
              <div className="text-[10px] text-white/55 mt-1">Total MG collected</div>
              <div className="mt-3 pt-3 border-t border-white/8 grid grid-cols-2 gap-3">
                <div>
                  <div className="text-[9px] uppercase tracking-[0.2em] text-white/40">Your share</div>
                  <div className="mt-0.5 text-sm text-white">{fmtMoney(t.agent, cur)}</div>
                </div>
                <div>
                  <div className="text-[9px] uppercase tracking-[0.2em] text-white/40">To producer</div>
                  <div className="mt-0.5 text-sm text-white">{fmtMoney(t.producer, cur)}</div>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Deals list */}
      <div className="mt-10">
        {loading ? (
          <div className="cs-card rounded-xl p-8 text-center text-white/55"
               data-testid="sales-agent-deals-loading">
            <Loader2 size={16} className="animate-spin inline mr-2"/> Loading deals…
          </div>
        ) : deals.length === 0 ? (
          <div className="cs-card rounded-xl p-10 text-center"
               data-testid="sales-agent-deals-empty">
            <Handshake size={30} className="mx-auto text-white/25 mb-4"/>
            <div className="text-white/70 text-sm">No deals logged yet.</div>
            <div className="text-white/45 text-[11px] mt-2 max-w-md mx-auto">
              Log your first deal — every sale you close in every territory,
              with MG, expenses and commission. Producers you represent see
              the same ledger.
            </div>
          </div>
        ) : (
          <div className="grid gap-4"
               data-testid="sales-agent-deals-list">
            {deals.map((d) => (
              <DealRow key={d.id} deal={d} onDelete={refresh}/>
            ))}
          </div>
        )}
      </div>

      {showAdd && (
        <AddDealModal
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); refresh(); }}
          meta={meta}
        />
      )}
    </div>
  );
}

function fmtMoney(n, cur) {
  return `${cur} ${Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;
}

/* ───────────────────────────────────────────────────────────── */
function DealRow({ deal, onDelete }) {
  const [busy, setBusy] = useState(false);
  const remove = async () => {
    if (!confirm(`Remove deal "${deal.film_title} · ${deal.territory}"?`)) return;
    setBusy(true);
    try {
      await api.delete(`/sales-agent/deals/${deal.id}`);
      toast.success("Deal removed.");
      onDelete();
    } catch {
      toast.error("Could not remove deal.");
    } finally { setBusy(false); }
  };
  return (
    <div className="cs-card rounded-xl p-5"
         data-testid={`sales-agent-deal-${deal.id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <div className="font-display text-lg uppercase tracking-tight">{deal.film_title}</div>
            {deal.film_year && (
              <span className="px-1.5 py-0.5 rounded-full bg-white/8 text-[9px] tracking-[0.16em] uppercase text-white/60">
                {deal.film_year}
              </span>
            )}
            <span className={`px-2 py-0.5 rounded-full text-[9px] tracking-[0.18em] uppercase font-semibold ${STATUS_TONE[deal.status] || "bg-white/8 text-white/60"}`}
                  data-testid={`sales-agent-deal-status-${deal.id}`}>
              {deal.status}
            </span>
          </div>
          <div className="mt-1.5 text-xs text-white/60">
            {deal.film_producer && `Producer · ${deal.film_producer} · `}
            <span className="text-orange-300 font-semibold">{deal.buyer}</span> ({deal.buyer_type}) · {WINDOW_LABELS[deal.window_kind]} rights for {deal.territory}
          </div>
        </div>
        <button onClick={remove} disabled={busy}
                className="text-white/40 hover:text-red-400 transition p-1"
                data-testid={`sales-agent-deal-delete-${deal.id}`}>
          {busy ? <Loader2 size={14} className="animate-spin"/> : <Trash2 size={14}/>}
        </button>
      </div>

      <div className="mt-4 pt-4 border-t border-white/8 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <SplitStat label="MG"           value={fmtMoney(deal.mg_amount, deal.mg_currency)}/>
        <SplitStat label="Expenses"     value={fmtMoney(deal.expenses_amount, deal.expenses_currency)}/>
        <SplitStat label={`Your ${deal.commission_pct}%`}
                   value={fmtMoney(deal.agent_share, deal.mg_currency)} accent="orange"/>
        <SplitStat label="To producer"  value={fmtMoney(deal.producer_share, deal.mg_currency)} accent="emerald"/>
      </div>

      {(deal.signed_date || deal.delivery_deadline || deal.notes) && (
        <div className="mt-3 pt-3 border-t border-white/6 text-[11px] text-white/55 flex flex-wrap gap-4">
          {deal.signed_date && <span>Signed · {deal.signed_date}</span>}
          {deal.delivery_deadline && <span>Deliver by · {deal.delivery_deadline}</span>}
          {deal.notes && <span className="italic text-white/45">“{deal.notes}”</span>}
        </div>
      )}
    </div>
  );
}

function SplitStat({ label, value, accent }) {
  const clr = accent === "orange" ? "text-orange-200" :
              accent === "emerald" ? "text-emerald-200" : "text-white";
  return (
    <div>
      <div className="text-[9px] uppercase tracking-[0.2em] text-white/40">{label}</div>
      <div className={`mt-0.5 text-sm font-semibold ${clr}`}>{value}</div>
    </div>
  );
}

/* ───────────────────────────────────────────────────────────── */
function AddDealModal({ onClose, onSaved, meta }) {
  const [form, setForm] = useState({
    film_title: "", film_producer: "", film_producer_org_id: "",
    film_year: "", film_language: "",
    buyer: "", buyer_type: "distributor",
    window_kind: "theatrical", territory: "",
    mg_amount: 0, mg_currency: "USD",
    commission_pct: 20, expenses_amount: 0, expenses_currency: "USD",
    signed_date: "", delivery_deadline: "",
    status: "signed", notes: "",
    contract_url: "", contract_signed_date: "",
    screener_link_id: "",
  });
  const [phList, setPhList] = useState([]);
  const [screeners, setScreeners] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/directory/orgs", { params: { type: "production_house" } })
      .then(({ data }) => setPhList(data.items || data.rows || data.orgs || (Array.isArray(data) ? data : [])))
      .catch(() => setPhList([]));
  }, []);

  // Load producer's active screeners when the SA picks a producer
  useEffect(() => {
    if (!form.film_producer_org_id) { setScreeners([]); return; }
    api.get(`/producer/${form.film_producer_org_id}/screeners/available`)
      .then(({ data }) => setScreeners(data.screeners || []))
      .catch(() => setScreeners([]));   // 403 = no linked deal yet
  }, [form.film_producer_org_id]);

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const num = (k) => (e) => setForm((f) => ({ ...f, [k]: Number(e.target.value) || 0 }));

  const submit = async () => {
    if (form.film_title.trim().length < 1) return toast.error("Film title is required.");
    if (form.buyer.trim().length < 1)      return toast.error("Buyer name is required.");
    if (form.territory.trim().length < 1)  return toast.error("Territory is required.");
    setBusy(true);
    try {
      await api.post("/sales-agent/deals", {
        ...form,
        film_year: form.film_year ? Number(form.film_year) : null,
      });
      toast.success("Deal logged.");
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not log deal.");
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4"
         onClick={onClose} data-testid="sales-agent-deals-add-modal">
      <div className="w-full max-w-3xl cs-card rounded-2xl p-6 sm:p-8 max-h-[90vh] overflow-y-auto"
           onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between gap-3 mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-orange-500/15 text-orange-300 flex items-center justify-center">
              <Handshake size={18}/>
            </div>
            <div>
              <div className="font-display text-lg uppercase tracking-tight">Log Deal</div>
              <div className="text-[11px] text-white/55">Every rights sale · one row per territory + window.</div>
            </div>
          </div>
          <button onClick={onClose} className="text-white/50 hover:text-white p-1"><X size={16}/></button>
        </div>

        {/* Film */}
        <SectionLabel>Film</SectionLabel>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Title *">
            <Input value={form.film_title} onChange={set("film_title")} className="bg-black/40 border-white/15"
                   data-testid="sales-agent-deal-title"/>
          </Field>
          <Field label="Producer">
            <Input value={form.film_producer} onChange={set("film_producer")}
                   placeholder="Free-text if not on Call Sheet"
                   className="bg-black/40 border-white/15"
                   data-testid="sales-agent-deal-producer"/>
          </Field>
          <Field label="Producer on Call Sheet (optional)">
            <select value={form.film_producer_org_id} onChange={set("film_producer_org_id")}
                    className="w-full h-10 px-3 rounded-md bg-black/40 border border-white/15 text-sm text-white"
                    data-testid="sales-agent-deal-producer-org">
              <option value="">— Not linked —</option>
              {phList.map((p) => (
                <option key={p.id} value={p.id}>{p.brand_name || p.legal_name}</option>
              ))}
            </select>
            <p className="mt-1.5 text-[10px] text-white/40 leading-relaxed">
              Linking gives the producer read-only visibility into MG + splits.
            </p>
          </Field>
          <Field label="Year">
            <Input value={form.film_year} onChange={set("film_year")} type="number" min={1900} max={2100}
                   className="bg-black/40 border-white/15"
                   data-testid="sales-agent-deal-year"/>
          </Field>
          <Field label="Language">
            <Input value={form.film_language} onChange={set("film_language")} placeholder="Hindi, Tamil, English…"
                   className="bg-black/40 border-white/15"
                   data-testid="sales-agent-deal-language"/>
          </Field>
        </div>

        {/* Buyer + Window */}
        <SectionLabel>Buyer & Rights</SectionLabel>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Buyer *">
            <Input value={form.buyer} onChange={set("buyer")} placeholder="Distributor / Broadcaster / Streamer"
                   className="bg-black/40 border-white/15"
                   data-testid="sales-agent-deal-buyer"/>
          </Field>
          <Field label="Buyer Type">
            <select value={form.buyer_type} onChange={set("buyer_type")}
                    className="w-full h-10 px-3 rounded-md bg-black/40 border border-white/15 text-sm text-white"
                    data-testid="sales-agent-deal-buyer-type">
              {(meta.buyer_types || []).map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </Field>
          <Field label="Window">
            <select value={form.window_kind} onChange={set("window_kind")}
                    className="w-full h-10 px-3 rounded-md bg-black/40 border border-white/15 text-sm text-white"
                    data-testid="sales-agent-deal-window">
              {Object.entries(WINDOW_LABELS).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </Field>
          <Field label="Territory *">
            <Input value={form.territory} onChange={set("territory")} placeholder="India · GCC · Australia…"
                   className="bg-black/40 border-white/15"
                   data-testid="sales-agent-deal-territory"/>
          </Field>
        </div>

        {/* Money */}
        <SectionLabel>Money</SectionLabel>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="MG Amount">
            <Input value={form.mg_amount} onChange={num("mg_amount")} type="number" min={0} step={100}
                   className="bg-black/40 border-white/15"
                   data-testid="sales-agent-deal-mg-amount"/>
          </Field>
          <Field label="MG Currency">
            <select value={form.mg_currency} onChange={set("mg_currency")}
                    className="w-full h-10 px-3 rounded-md bg-black/40 border border-white/15 text-sm text-white"
                    data-testid="sales-agent-deal-mg-currency">
              {(meta.currencies || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Commission %">
            <Input value={form.commission_pct} onChange={num("commission_pct")} type="number" min={0} max={100} step={0.5}
                   className="bg-black/40 border-white/15"
                   data-testid="sales-agent-deal-commission"/>
          </Field>
          <Field label="Expenses">
            <Input value={form.expenses_amount} onChange={num("expenses_amount")} type="number" min={0} step={100}
                   className="bg-black/40 border-white/15"
                   data-testid="sales-agent-deal-expenses-amount"/>
          </Field>
          <Field label="Expenses Currency">
            <select value={form.expenses_currency} onChange={set("expenses_currency")}
                    className="w-full h-10 px-3 rounded-md bg-black/40 border border-white/15 text-sm text-white"
                    data-testid="sales-agent-deal-expenses-currency">
              {(meta.currencies || []).map((c) => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>
          <Field label="Status">
            <select value={form.status} onChange={set("status")}
                    className="w-full h-10 px-3 rounded-md bg-black/40 border border-white/15 text-sm text-white"
                    data-testid="sales-agent-deal-status">
              {(meta.statuses || []).map((s) => <option key={s} value={s}>{s}</option>)}
            </select>
          </Field>
        </div>

        {/* Dates */}
        <SectionLabel>Timeline</SectionLabel>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Signed Date">
            <Input value={form.signed_date} onChange={set("signed_date")} type="date"
                   className="bg-black/40 border-white/15"
                   data-testid="sales-agent-deal-signed-date"/>
          </Field>
          <Field label="Delivery Deadline">
            <Input value={form.delivery_deadline} onChange={set("delivery_deadline")} type="date"
                   className="bg-black/40 border-white/15"
                   data-testid="sales-agent-deal-delivery-date"/>
          </Field>
        </div>

        {/* Attachments */}
        <SectionLabel>Attachments</SectionLabel>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Signed Contract URL">
            <Input value={form.contract_url} onChange={set("contract_url")}
                   placeholder="https://drive.google.com/… · https://dropbox.com/…"
                   className="bg-black/40 border-white/15"
                   data-testid="sales-agent-deal-contract-url"/>
          </Field>
          <Field label="Contract Signed Date">
            <Input value={form.contract_signed_date} onChange={set("contract_signed_date")} type="date"
                   className="bg-black/40 border-white/15"
                   data-testid="sales-agent-deal-contract-date"/>
          </Field>
          {form.film_producer_org_id && (
            <div className="sm:col-span-2">
              <Field label="Attach Producer Screener (optional)">
                {screeners.length > 0 ? (
                  <select value={form.screener_link_id} onChange={set("screener_link_id")}
                          className="w-full h-10 px-3 rounded-md bg-black/40 border border-white/15 text-sm text-white"
                          data-testid="sales-agent-deal-screener">
                    <option value="">— None —</option>
                    {screeners.map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.film_title}{s.film_year ? ` (${s.film_year})` : ""}
                      </option>
                    ))}
                  </select>
                ) : (
                  <div className="text-[11px] text-white/45 italic px-1 py-2"
                       data-testid="sales-agent-deal-screener-empty">
                    This producer hasn&apos;t added a screener yet, or hasn&apos;t
                    linked one to you. Log the deal now — you can attach
                    a screener later.
                  </div>
                )}
              </Field>
            </div>
          )}
        </div>

        <div className="mt-4">
          <Field label="Notes">
            <Textarea value={form.notes} onChange={set("notes")} className="bg-black/40 border-white/15 min-h-[60px]"
                      data-testid="sales-agent-deal-notes"/>
          </Field>
        </div>

        <div className="mt-8 flex gap-3">
          <Button variant="ghost" onClick={onClose}
                  className="h-11 rounded-full text-[11px] uppercase tracking-[0.22em] text-white/70 hover:bg-white/5">
            Cancel
          </Button>
          <Button className="flex-1 h-11 rounded-full text-[11px] uppercase tracking-[0.22em] bg-orange-600 text-white hover:bg-orange-500 disabled:opacity-50"
                  disabled={busy} onClick={submit}
                  data-testid="sales-agent-deal-save">
            {busy ? <><Loader2 size={13} className="animate-spin mr-2"/> Saving…</> : "Save Deal"}
          </Button>
        </div>
      </div>
    </div>
  );
}

function SectionLabel({ children }) {
  return (
    <div className="mt-6 mb-3 text-[10px] tracking-[0.28em] uppercase text-white/45">{children}</div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <Label className="text-[10px] uppercase tracking-[0.22em] text-white/55">{label}</Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
